import React, { useContext, useEffect, useRef, useState } from 'react';
import { createBrowserHistory } from 'history';
import { Form } from 'react-final-form';
import _ from 'lodash';
import { useParams } from 'react-router-dom';
import buildResponses from '@unite-us/surveyjs/dist/components/Renderer/buildResponses';
import TrackerContext from '../utils/context';
import eventConstants from '../utils/eventConstants';
import callOrLog from '../utils/callOrLog';
import '@unite-us/ui/dist/main.css';
import SurveyJSForm from '../utils/index';
import PersonalForm from './components/PersonalForm';
import MilitaryForm from './components/MilitaryForm';
import Consent from './components/Signature/Consent';
import AssistanceRequestForm from './components/AssistanceRequestForm';
import useAssistanceRequest from './useAssistanceRequest';
import { createAssistanceRequest } from '../actions';
import formUtils from '../utils/formUtils';
import enums from '../constants/defaultEnums';
import { CORE_API, FL_PROVIDER_ID } from '../env.config';
import './AssistanceRequest.css';
import translations from '../constants/translations';

function AssistanceRequest() {
  const { token: legacyToken, coreToken, providerId } = useParams();
  const languages = enums.base.languages.filter((language) => language.enabled === true);
  // Split the provider configuration value into an array and search for the loaded provider id
  const floridaProviderIds = FL_PROVIDER_ID ? FL_PROVIDER_ID.split(',') : [];
  const showLanguageSelector = _.findIndex(floridaProviderIds, (flProviderId) => flProviderId === providerId) > -1;
  const [state, setState] = useState({
    signature_image: null,
    failedSubmission: false,
    language: 'en',
  });

  const [hasErrors, setHasErrors] = useState(false);

  useEffect(() => setHasErrors(surveyRef.current?.hasErrors()), [hasErrors])

  let initialCoreTokenData = null;
  if (coreToken && providerId) {
    initialCoreTokenData = {
      api_key: null,
      provider_id: providerId,
      token: coreToken,
    };
  }

  const { signature_image } = state;
  const data = useAssistanceRequest(legacyToken, initialCoreTokenData, state.language);
  const history = createBrowserHistory({ forceRefresh: true });
  const surveyRef = useRef();
  const eventTracker = useContext(TrackerContext);

  const setBase64SignatureImage = (newSignature) => {
    setState({ ...state, signature_image: newSignature });
  };

  const goToFirstError = () => {
    setHasErrors(surveyRef.current?.hasErrors())
    const errs = [...document.querySelectorAll('.ui-form-field__error')]
      .filter((a) => a.textContent.includes('Required'));
    if (errs && errs.length > 0) {
      const err = _.first(errs);
      err.parentElement.parentNode.scrollIntoView();
    }
  };

  const languageChange = (prop) => {
    setState({ language: prop });
  };

  const handleSetSurvey = (survey) => {
    surveyRef.current = survey;
  };

  const onSubmit = async (response) => {
    const customForm = _.get(data, 'state.customForm.data.data', null);
    const { Rollbar } = window;
    if (
      _.isString(response.gross_monthly_income)
      && _.isEmpty(response.gross_monthly_income)
    ) {
      _.set(response, 'gross_monthly_income', null);
    }
    const coreToken = _.get(data, 'state.coreTokenData', null);
    const ref = surveyRef;
    const customFormResponses = ref && ref.current ? buildResponses(ref.current) : [];
    const contactFullName = `${_.get(response, 'first_name')} ${_.get(response, 'last_name')}`;

    const pdfFile = await formUtils.generatePDFDynamic({
      base64SignatureImage: signature_image,
      fullName: 'name',
      htmlContent: data.state.consentAgreement.content,
      contactFullName,
    });

    const requestData = {
      assistance_request: {
        description: response.description,
        ...(
          customForm ? {
            form_id: customForm.id,
            form_response: _.map(customFormResponses, (r) => ({
              name: r.name,
              title: r.title,
              value: r.value,
              displayValue: r.displayValue,
            })),
          } : null
        ),
        requestor: response,
        service_type_id: response.service_types,
      },
      language: state.language,
    };

    if (!surveyRef.current?.hasErrors()) {
      return createAssistanceRequest(coreToken, pdfFile, requestData).then((r) => {
        const consent_file_upload_url = _.get(r, 'data.consent_file_upload_url', {});
        if (_.isEmpty(consent_file_upload_url)) {
          setState({ failedSubmission: true });
          if (Rollbar) { Rollbar.error('Failed to submit the form.', r); }
        } else {
          history.push({
            pathname: `/assistance-request/${data.state.legacyToken}/success`,
            state: {
              consent: `${CORE_API.split('/v1')[0]}${consent_file_upload_url}`,
              language: state.language,
            },
          });
          setState({ failedSubmission: false });
          callOrLog(() => eventTracker(
            eventConstants.ASSISTANCE_REQUEST.created,
            null,
            { consent_file_upload_url },
          ));
        }
      });
    }
  };

  let assistanceReqConfigs = _.get(data, 'state.assistanceReqConfigs', null);

  if (assistanceReqConfigs !== null) {
    assistanceReqConfigs.language = state.language;
  } else {
    assistanceReqConfigs = { language: state.language };
  }
  const customForm = _.get(data, 'state.customForm.data.data', null);
  const serviceTypes = _.get(data, 'state.serviceTypes', null);

  if (data.isLoading) {
    return <p>Loading..</p>;
  }

  if (data.error) {
    return <p>Error..</p>;
  }

  return (
    <div className="m-10">
      <Form
        onSubmit={onSubmit}
        initialValues={{}}
        render={({
          handleSubmit, form, submitting,
        }) => (
          <form onSubmit={handleSubmit}>
            {showLanguageSelector &&
            <div className="mb-4">
              <span className='mb-2 font-family-["Avenir_Heavy"] text-[#506784] text-[12px] font-bold'>LANGUAGE</span>
              <div className="">
                <button className={`mr-4 mb-2 ${state.language == 'en' ? 'bg-slate-400' : 'bg-white border-slate-400 border-[1px]'} text-slate-900 rounded-lg font-bold px-8 py-2`} type="button" onClick={() => languageChange('en')}>
                  English
                </button>
                <button className={`mr-4 mb-2 ${state.language == 'es' ? 'bg-slate-400' : 'bg-white border-slate-400 border-[1px]'} text-slate-900 rounded-lg font-bold px-8 py-2`} type="button" onClick={() => languageChange('es')}>
                  Spanish (Español)
                </button>
                <button className={`mr-4 mb-2 ${state.language == 'ht' ? 'bg-slate-400' : 'bg-white border-slate-400 border-[1px]'} text-slate-900 rounded-lg font-bold px-8 py-2`} type="button" onClick={() => languageChange('ht')}>
                  Haitian Creole (Kreyòl)
                </button>
              </div>
            </div>
            }
            <PersonalForm assistanceReqConfigs={assistanceReqConfigs} />
            <MilitaryForm form={form} assistanceReqConfigs={assistanceReqConfigs} />
            {customForm && (
            <SurveyJSForm
              id="custom-form"
              form={customForm.attributes}
              formRef={handleSetSurvey}
              expanded
            />
            )}

            <h3 className='services mb-2 ui-h3-heavy'>Services</h3>
            {serviceTypes && <AssistanceRequestForm serviceTypes={serviceTypes} language={state.language} />}
            <div className="form-group">
              <Consent handleSignatureEnd={setBase64SignatureImage} failedSubmission={state.failedSubmission} language={state.language} />
              {!signature_image ? (
                <div
                  className="mt-2 mb-5 text-red-700"
                  style={{ marginTop: '-40px' }}
                >
                  {_.get(translations, `${state.language}.signatureRequired`)}
                </div>
              ) : ''}
            </div>
            <div className="flex">
              <div>
                {!submitting ? (
                  <button className="bg-blue-900 text-white font-bold px-8 py-2 mr-8 mb-4" type="submit" onClick={() => goToFirstError()}>
                    {_.get(translations, `${state.language}.submit`)}
                  </button>
                ) : <p>{_.get(translations, `${state.language}.submitting`)}</p>}
              </div>
              <div>
                {state.failedSubmission ? (
                  <div
                    className="font-bold text-red-700 mt-2"
                  >
                    {_.get(translations, `${state.language}.failedSubmission`)}
                  </div>
                ) : null}
              </div>
            </div>
          </form>
        )}
      />
    </div>
  );
}

export default AssistanceRequest;
